'use client';

import Image from 'next/image';
import Link from 'next/link';
import { useSession } from 'next-auth/react';

interface BookCardProps {
  book: {
    id: string;
    title: string;
    description?: string | null;
    price: number;
    coverImage?: string | null;
    status: string;
    author: {
      id: string;
      name: string;
    };
  };
  showStatus?: boolean;
  showActions?: boolean;
  onApprove?: (id: string) => void;
  onReject?: (id: string) => void;
}

export default function BookCard({
  book,
  showStatus = false,
  showActions = false,
  onApprove,
  onReject,
}: BookCardProps) {
  const { data: session } = useSession();

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'APPROVED':
        return 'bg-green-100 text-green-800';
      case 'PENDING':
        return 'bg-yellow-100 text-yellow-800';
      case 'REJECTED':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300">
      <div className="relative h-48 bg-gradient-to-br from-indigo-500 to-purple-600">
        {book.coverImage ? (
          <Image
            src={book.coverImage}
            alt={book.title}
            fill
            className="object-cover"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-white">
            <svg className="w-16 h-16 opacity-50" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
            </svg>
          </div>
        )}
        {showStatus && (
          <span
            className={`absolute top-2 right-2 px-2 py-1 text-xs font-semibold rounded ${getStatusColor(
              book.status
            )}`}
          >
            {book.status}
          </span>
        )}
      </div>

      <div className="p-4">
        <h3 className="text-lg font-semibold text-gray-900 truncate">{book.title}</h3>
        <p className="text-sm text-gray-500 mt-1">by {book.author.name}</p>
        
        {session && book.description && (
          <p className="text-sm text-gray-600 mt-2 line-clamp-2">{book.description}</p>
        )}

        <div className="mt-4 flex items-center justify-between">
          {session ? (
            <span className="text-lg font-bold text-indigo-600">${book.price.toFixed(2)}</span>
          ) : (
            <span className="text-sm text-gray-500">Sign in to see price</span>
          )}

          {showActions && session?.user.role === 'ADMIN' && book.status === 'PENDING' ? (
            <div className="flex space-x-2">
              <button
                onClick={() => onApprove?.(book.id)}
                className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700"
              >
                Approve
              </button>
              <button
                onClick={() => onReject?.(book.id)}
                className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700"
              >
                Reject
              </button>
            </div>
          ) : (
            <Link
              href={`/books/${book.id}`}
              className="px-4 py-2 bg-indigo-600 text-white text-sm rounded-md hover:bg-indigo-700 transition-colors"
            >
              View Details
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
